import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  ShieldAlert,
  Phone,
  AlertTriangle,
  Send,
  MapPin,
  CheckCircle2,
  Loader2,
  X,
  Volume2,
  VolumeX,
  Eye,
  Users,
  UserCheck,
  ChevronRight
} from 'lucide-react';
import { HELPLINES, EMERGENCY_TYPES } from './helplineData';
import { sirenPlayer } from './EmergencySiren';
import { toast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

import { BACKEND_URL } from '../../config';
const API_BASE = BACKEND_URL;

const TOTAL_COUNTDOWN = 5;

export const SosTriggerModal = ({ isOpen, onClose, defaultTrain = '', defaultStation = '' }) => {
  const nav = useNavigate();
  const [step, setStep] = useState('countdown'); // 'phone_prompt' | 'countdown' | 'form' | 'success'
  const [countdown, setCountdown] = useState(TOTAL_COUNTDOWN);
  const [countdownActive, setCountdownActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [alertResult, setAlertResult] = useState(null);

  // Form states
  const [mobile, setMobile] = useState('');
  const [passengerName, setPassengerName] = useState('');
  const [emergencyType, setEmergencyType] = useState('Medical Emergency');
  const [trainNumber, setTrainNumber] = useState(defaultTrain);
  const [coach, setCoach] = useState('');
  const [berth, setBerth] = useState('');
  const [station, setStation] = useState(defaultStation);
  const [message, setMessage] = useState('');
  const [location, setLocation] = useState(null);
  const [fetchingGps, setFetchingGps] = useState(false);
  const [sirenActive, setSirenActive] = useState(false);

  const timerRef = useRef(null);
  const alertSentRef = useRef(false);

  // Automatically fetch GPS and verify phone when modal opens
  useEffect(() => {
    if (isOpen) {
      alertSentRef.current = false;
      setCountdown(TOTAL_COUNTDOWN);
      setAlertResult(null);

      // 1. Resolve User Mobile Number (SSO only)
      let resolvedMobile = '';
      let resolvedName = '';

      const u = localStorage.getItem('tp_user');
      if (u) {
        try {
          const parsed = JSON.parse(u);
          if (parsed.mobile) resolvedMobile = parsed.mobile;
          if (parsed.name) resolvedName = parsed.name;
        } catch (_) {}
      }

      if (resolvedMobile) {
        setMobile(resolvedMobile);
        if (resolvedName) setPassengerName(resolvedName);
        setStep('countdown');
        setCountdownActive(true);
      } else {
        // If not logged in, always prompt for fresh mobile number on each session
        setMobile('');
        setPassengerName('');
        setStep('phone_prompt');
        setCountdownActive(false);
      }

      // 2. Fetch Live GPS Coordinates
      if (navigator.geolocation) {
        setFetchingGps(true);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setLocation({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              acc: pos.coords.accuracy,
            });
            setFetchingGps(false);
          },
          () => {
            // Default fallback coordinates (New Delhi Station area)
            setLocation({ lat: 28.6139, lng: 77.2090, acc: 20 });
            setFetchingGps(false);
          },
          { timeout: 7000, enableHighAccuracy: true }
        );
      }
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      sirenPlayer.stop();
      setSirenActive(false);
      alertSentRef.current = false;
    }
  }, [isOpen]);

  // Countdown timer interval (pure state update, no side effects inside updater)
  useEffect(() => {
    if (!isOpen || step !== 'countdown' || !countdownActive) return;

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, step, countdownActive]);

  // When countdown hits 0, trigger alert exactly ONCE
  useEffect(() => {
    if (isOpen && step === 'countdown' && countdownActive && countdown === 0) {
      if (!alertSentRef.current) {
        alertSentRef.current = true;
        executeSosAlert();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, countdownActive, countdown]);

  const cancelCountdown = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    alertSentRef.current = true;
    setCountdownActive(false);
    setStep('form');
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    const clean = mobile.replace(/\D/g, '');
    if (clean.length < 10) {
      toast({
        title: 'Valid 10-digit number required',
        description: 'Please enter a valid mobile number for emergency callback.',
        variant: 'destructive',
      });
      return;
    }
    localStorage.removeItem('tp_guest_mobile');
    setMobile(clean);
    setCountdown(TOTAL_COUNTDOWN);
    setStep('countdown');
    setCountdownActive(true);
  };

  const executeSosAlert = async () => {
    const cleanMobile = mobile.replace(/\D/g, '');
    if (cleanMobile.length < 10) {
      setStep('phone_prompt');
      setCountdownActive(false);
      toast({
        title: 'Mobile number required',
        description: 'Please provide your 10-digit mobile number to dispatch SOS.',
        variant: 'destructive',
      });
      return;
    }

    if (timerRef.current) clearInterval(timerRef.current);
    alertSentRef.current = true;
    setLoading(true);
    const token = localStorage.getItem('tp_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const res = await axios.post(
        `${API_BASE}/api/sos/trigger`,
        {
          passenger_name: passengerName || 'Passenger',
          mobile: cleanMobile,
          emergency_type: emergencyType,
          train_number: trainNumber,
          coach: coach,
          berth: berth,
          station: station,
          message: message,
          latitude: location?.lat || 28.6139,
          longitude: location?.lng || 77.2090,
          accuracy_meters: location?.acc || 15,
        },
        { headers }
      );

      setAlertResult(res.data);
      setStep('success');

      const famCount = res.data.family_members_notified?.length || 0;
      toast({
        title: '🚨 EMERGENCY SOS ALERT BROADCASTED',
        description: famCount > 0
          ? `Alert & GPS sent to Admin and ${famCount} Family Members!`
          : `Distress alert & live GPS coordinates logged with Railway Response!`,
      });
    } catch (err) {
      toast({
        title: 'Error triggering online alert',
        description: 'Please dial 182 or 112 immediately!',
        variant: 'destructive',
      });
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  const toggleSiren = () => {
    if (sirenActive) {
      sirenPlayer.stop();
      setSirenActive(false);
    } else {
      sirenPlayer.play(15);
      setSirenActive(true);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto animate-in fade-in duration-200">
      <div className="relative w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-red-500 my-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 via-rose-600 to-red-700 p-5 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-inner">
              <ShieldAlert className="w-6 h-6 text-white animate-pulse" />
            </div>
            <div>
              <h2 className="text-lg font-black tracking-tight flex items-center gap-2">
                Emergency SOS Alert
              </h2>
              <p className="text-xs text-red-100 font-medium">Instant Family & Railways Help Dispatch</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          
          {/* STEP 0: MANDATORY PHONE NUMBER PROMPT (SOLUTION 1) */}
          {step === 'phone_prompt' && (
            <form onSubmit={handlePhoneSubmit} className="py-2 space-y-5">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                  <Phone className="w-7 h-7 animate-bounce" />
                </div>
                <h3 className="text-xl font-black text-gray-900">
                  Enter Your Contact Mobile Number
                </h3>
              </div>

              <div className="bg-red-50/70 border border-red-100 rounded-2xl p-4 space-y-3">
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-red-600" /> Mobile Number *
                  </label>
                  <div className="mt-1 flex items-center rounded-xl border border-gray-300 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-red-500">
                    <span className="px-3.5 py-2.5 bg-gray-100 text-gray-700 font-bold text-sm border-r border-gray-200">
                      🇮🇳 +91
                    </span>
                    <input
                      type="tel"
                      autoFocus
                      required
                      maxLength={10}
                      placeholder="Enter 10-digit number"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-3.5 py-2.5 text-sm font-semibold tracking-wide outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-gray-500" /> Passenger Name (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Rahul Sharma"
                    value={passengerName}
                    onChange={(e) => setPassengerName(e.target.value)}
                    className="mt-1 w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>
              </div>

              {/* GPS Live Telemetry Preview */}
              <div className="flex items-center justify-between text-xs px-2 text-gray-600">
                <span className="flex items-center gap-1.5 font-medium">
                  <MapPin className="w-4 h-4 text-red-600 animate-pulse" />
                  {fetchingGps ? 'Locking GPS satellite coordinates...' : location ? `Live GPS Locked (±${Math.round(location.acc || 15)}m)` : 'GPS Ready'}
                </span>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-1/3 py-3 px-4 rounded-2xl border-2 border-gray-200 hover:bg-gray-100 text-gray-700 font-bold text-xs transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 px-4 rounded-2xl bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-black text-sm shadow-lg shadow-red-600/30 flex items-center justify-center gap-2 transition-all"
                >
                  Proceed to SOS Alert <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {/* STEP 1: COUNTDOWN SCREEN (Accidental Trigger Protection) */}
          {step === 'countdown' && (
            <div className="text-center py-4 space-y-6">
              <div className="space-y-2">
                <span className="inline-block px-3 py-1 rounded-full bg-red-100 text-red-700 font-extrabold text-xs uppercase tracking-wider">
                  ⚠️ Accidental Protection
                </span>
                <h3 className="text-2xl font-black text-gray-900">
                  Triggering SOS Distress Signal in...
                </h3>
                <p className="text-xs text-gray-500 max-w-md mx-auto">
                  Live GPS location and emergency push siren will be dispatched to Railway Response & Family Contacts.
                </p>
              </div>

              {/* Countdown Circular Ring */}
              <div className="relative w-36 h-36 mx-auto flex items-center justify-center">
                <div className="absolute inset-0 rounded-full border-8 border-red-100"></div>
                <div
                  className="absolute inset-0 rounded-full border-8 border-red-600 border-t-transparent animate-spin"
                  style={{ animationDuration: '1s' }}
                ></div>
                <span className="text-5xl font-black text-red-600 font-mono">{countdown}</span>
              </div>

              {/* Verified Passenger & GPS Telemetry Pill */}
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-2xl bg-gray-50 border border-gray-200 text-xs font-semibold text-gray-700">
                <span className="flex items-center gap-1 text-red-600">
                  <Phone className="w-3.5 h-3.5" /> +91 {mobile || 'Registered'}
                </span>
                <span className="text-gray-300">|</span>
                <span className="flex items-center gap-1 text-emerald-700">
                  <MapPin className="w-3.5 h-3.5" /> GPS Active
                </span>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={cancelCountdown}
                  className="flex-1 py-3 px-4 rounded-2xl border-2 border-gray-300 hover:bg-gray-100 text-gray-700 font-bold text-sm transition-all"
                >
                  Cancel / Edit Details First
                </button>
                <button
                  onClick={executeSosAlert}
                  disabled={loading}
                  className="flex-1 py-3 px-4 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-black text-sm shadow-lg shadow-red-600/30 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Alert Instantly'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: DETAILS FORM */}
          {step === 'form' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                executeSosAlert();
              }}
              className="space-y-4"
            >
              <div className="p-3.5 bg-red-50 rounded-2xl border border-red-100 text-xs text-red-900 flex items-start gap-2.5">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <p>
                  Fill in your coach & seat information. When sent, all emergency response channels will receive your live map tracking pin and emergency alarm.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">
                    Contact Mobile *
                  </label>
                  <input
                    type="tel"
                    required
                    maxLength={10}
                    placeholder="10-digit number"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                    className="mt-1 w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">
                    Emergency Nature *
                  </label>
                  <select
                    value={emergencyType}
                    onChange={(e) => setEmergencyType(e.target.value)}
                    className="mt-1 w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-red-500 outline-none"
                  >
                    {EMERGENCY_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">
                    Passenger Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Rahul Sharma"
                    value={passengerName}
                    onChange={(e) => setPassengerName(e.target.value)}
                    className="mt-1 w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">
                    Train Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 12951 / Rajdhani"
                    value={trainNumber}
                    onChange={(e) => setTrainNumber(e.target.value)}
                    className="mt-1 w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Coach</label>
                  <input
                    type="text"
                    placeholder="e.g. B2"
                    value={coach}
                    onChange={(e) => setCoach(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-xl text-sm uppercase focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Berth / Seat</label>
                  <input
                    type="text"
                    placeholder="e.g. 42"
                    value={berth}
                    onChange={(e) => setBerth(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Nearest Station</label>
                  <input
                    type="text"
                    placeholder="e.g. BPL"
                    value={station}
                    onChange={(e) => setStation(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-xl text-sm uppercase focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">
                  Emergency Situation Details
                </label>
                <textarea
                  rows={2}
                  placeholder="Need medical help / Security intervention..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="mt-1 w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none"
                />
              </div>

              <div className="flex items-center justify-between text-xs text-gray-600">
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-red-600" />
                  {fetchingGps ? 'Locating GPS...' : location ? `GPS Locked (Accuracy: ±${Math.round(location.acc || 15)}m)` : 'GPS Ready'}
                </span>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-4 rounded-2xl bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-red-500/25"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Broadcast SOS Now</>}
              </button>
            </form>
          )}

          {/* STEP 3: ALERT SENT SUCCESS SCREEN */}
          {step === 'success' && alertResult && (
            <div className="text-center py-3 space-y-5">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                <CheckCircle2 className="w-9 h-9" />
              </div>

              <div className="space-y-1">
                <h3 className="text-xl font-black text-gray-900">🚨 SOS Alert Active & Dispatched</h3>
                <p className="text-xs text-gray-500 font-mono">Reference ID: {alertResult.alert_id}</p>
                <p className="text-xs text-gray-700 font-semibold">Contact: +91 {mobile}</p>
              </div>

              {/* Emergency Broadcast Status Banner */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-left flex items-center gap-3">
                <div className="w-9 h-9 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldAlert className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-emerald-950">Broadcast Dispatched</p>
                  <p className="text-[11px] text-emerald-800">
                    Live GPS coordinates and distress signal transmitted to Railway Emergency Team & your family contacts.
                  </p>
                </div>
              </div>

              {/* Notified Family Members Card */}
              {alertResult.family_members_notified?.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3.5 text-left space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-gray-800">
                    <Users className="w-4 h-4 text-orange-600" />
                    <span>Notified Family Circle ({alertResult.family_members_notified.length} members):</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {alertResult.family_members_notified.map((m) => (
                      <span key={m.id} className="text-[11px] font-semibold bg-white border border-gray-200 px-2.5 py-1 rounded-lg text-gray-700">
                        {m.name} ({m.relationship})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 hover:bg-gray-100 text-gray-700 font-bold text-xs flex items-center justify-center transition-all"
                >
                  Close Window
                </button>

                <button
                  onClick={() => {
                    onClose();
                    nav(alertResult.tracking_url);
                  }}
                  className="flex-1 py-3 px-4 rounded-xl bg-gray-900 hover:bg-black text-white font-bold text-xs flex items-center justify-center gap-2 shadow"
                >
                  <Eye className="w-4 h-4" /> Open Live Map Tracking
                </button>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>,
    document.body
  );
};

export default SosTriggerModal;
